import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, X, Search } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import { TableSkeleton, ErrorState } from '../../components/ui';
import usePayPermissions, { needsPermission } from '../../hooks/usePayPermissions';

const KINDS = [
  { value: 'individual', label: 'Individual' },
  { value: 'group',      label: 'Group' },
  { value: 'sil',        label: 'SIL' },
  { value: 'transport',  label: 'Transport' },
  { value: 'expense',    label: 'Expense' },
];
const AH = [
  { value: 'end',     label: 'At shift end' },
  { value: 'start',   label: 'At shift start' },
  { value: 'split',   label: 'Split shift' },
  { value: 'highest', label: 'Highest rate' },
];
const CLAIMS = [
  { value: '',                 label: 'Standard' },
  { value: 'non_face_to_face', label: 'Non face-to-face' },
  { value: 'telehealth',       label: 'Telehealth' },
  { value: 'irregular_sil',    label: 'Irregular SIL' },
];
// Rows only badge what differs from the default. Individual and Active are what almost every row
// is, so a blue pill and a green pill on every line said nothing; now a Group, SIL, Transport or
// Expense kind, or an Inactive service type, is the thing that stands out.
const KIND_LABEL = Object.fromEntries(KINDS.map(k => [k.value, k.label]));
// Pay method (Pay Rates, owner spec 4.4): Hourly is paid from approved clock times by category;
// Sleepover is one fixed amount per shift. Used only by companies that pay from Pay Rates.
const PAY_METHODS = [
  { value: 'hourly',    label: 'Hourly' },
  { value: 'sleepover', label: 'Sleepover' },
];
const payMethodOf = (st) => (st && st.pay_method === 'sleepover' ? 'sleepover' : 'hourly');
const QUIET_BADGE = 'inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700 ring-1 ring-inset ring-slate-200';

function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className={`relative bg-white rounded-2xl shadow-2xl w-full ${wide ? 'max-w-2xl' : 'max-w-lg'} max-h-[88vh] overflow-y-auto`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white z-10">
          <h2 className="text-base font-semibold text-slate-800">{title}</h2>
          <button onClick={onClose} aria-label="Close" className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"><X size={16} /></button>
        </div>
        <div className="px-6 py-5 space-y-5">{children}</div>
      </div>
    </div>
  );
}
function Field({ label, required, children, hint }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}{required && <span className="text-red-400 ml-0.5">*</span>}</label>
      {children}
      {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}
function Toggle({ checked, onChange, label }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} className="flex items-center gap-2 text-sm text-slate-700">
      <span className={`w-9 h-5 rounded-full transition-colors relative ${checked ? 'bg-primary-500' : 'bg-slate-300'}`}>
        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${checked ? 'left-4.5 translate-x-0' : 'left-0.5'}`} style={{ left: checked ? '18px' : '2px' }} />
      </span>
      {label}
    </button>
  );
}
function GroupTitle({ children }) {
  return <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400 border-b border-slate-100 pb-1">{children}</h3>;
}

const EMPTY = {
  name: '', code: '', color: '#6366f1', categoryLabel: '', serviceKind: 'individual', claimType: '',
  supportItemNumber: '', supportCategory: '', agedCareItemCode: '', ahCalcMethod: 'end', ahStart: '', ahEnd: '',
  minStaff: 1, maxStaff: 1, silRatio: '', silCode: '',
  maxTravelKm: 0, maxTravelTimeMin: 0, maxTransportKm: 0, travelKmCode: '', transportKmCode: '', travelChargePct: 100,
  restrictByQual: false, payTravel: false, taxable: false, billable: true, isActive: true,
  quals: [], payMethod: 'hourly',
};

// Saves a type's pay method through the Pay Rates API, which owns it (admin only).
async function savePayMethod(id, value) {
  await api.put(`/pay-rates/shift-types/${id}`, { pay_method: value });
}

export default function ServiceTypes() {
  const confirm = useConfirm();
  // Setting a shift type's pay method needs Shift Types: Edit (Pay Rates v1.2 section 10).
  const { can } = usePayPermissions();
  const canPayMethod = can('shift_types.edit');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);   // { editing? } or null
  const [form, setForm] = useState(EMPTY);
  const [qualInput, setQualInput] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    // A failed load says so (with a retry) instead of showing "No service types yet".
    try { const { data } = await api.get('/service-types'); setList(Array.isArray(data) ? data : []); setLoadFailed(false); }
    catch { setLoadFailed(true); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openNew = () => { setForm(EMPTY); setQualInput(''); setModal({}); };
  const openEdit = (st) => {
    setForm({
      name: st.name || '', code: st.code || '', color: st.color || '#6366f1',
      categoryLabel: st.category || '', serviceKind: st.service_kind || 'individual', claimType: st.claim_type || '',
      supportItemNumber: st.support_item_number || '', supportCategory: st.support_category || '',
      agedCareItemCode: st.aged_care_item_code || '',
      ahCalcMethod: st.ah_calc_method || 'end', ahStart: st.ah_start || '', ahEnd: st.ah_end || '',
      minStaff: st.min_staff ?? 1, maxStaff: st.max_staff ?? 1, silRatio: st.sil_ratio || '', silCode: st.sil_code || '',
      maxTravelKm: st.max_travel_km ?? 0, maxTravelTimeMin: st.max_travel_time_min ?? 0, maxTransportKm: st.max_transport_km ?? 0,
      travelKmCode: st.travel_km_code || '', transportKmCode: st.transport_km_code || '', travelChargePct: st.travel_charge_pct ?? 100,
      restrictByQual: !!st.restrict_by_qual, payTravel: !!st.pay_travel, taxable: !!st.taxable,
      billable: st.billable !== false, isActive: st.is_active !== false,
      quals: Array.isArray(st.quals) ? st.quals : [],
      payMethod: payMethodOf(st),
    });
    setQualInput('');
    setModal({ editing: st });
  };

  const addQual = () => {
    const q = qualInput.trim();
    if (q && !form.quals.includes(q)) set('quals', [...form.quals, q]);
    setQualInput('');
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Name is required');
    if (Number(form.maxStaff) < Number(form.minStaff)) return toast.error('Max staff must be ≥ min staff');
    setSaving(true);
    try {
      const body = { ...form, pay_method: form.payMethod };
      if (modal.editing) {
        await api.put(`/service-types/${modal.editing.id}`, body);
        if (canPayMethod && form.payMethod !== payMethodOf(modal.editing)) await savePayMethod(modal.editing.id, form.payMethod);
        toast.success('Service type updated');
      } else {
        const { data } = await api.post('/service-types', body);
        if (canPayMethod && data?.id && form.payMethod !== payMethodOf(data)) await savePayMethod(data.id, form.payMethod);
        toast.success('Service type created');
      }
      setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const del = async (st) => {
    if (!await confirm(`Delete "${st.name}"? If it's used by shifts it will be deactivated instead.`, { title: 'Delete service type?', confirmLabel: 'Delete', danger: true })) return;
    try { const { data } = await api.delete(`/service-types/${st.id}`); toast.success(data.message || 'Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  const [payBusy, setPayBusy] = useState({});
  const changePayMethod = async (st, value) => {
    setPayBusy(b => ({ ...b, [st.id]: true }));
    setList(l => l.map(x => x.id === st.id ? { ...x, pay_method: value } : x));
    try {
      await savePayMethod(st.id, value);
      toast.success(`${st.name} is paid ${value === 'sleepover' ? 'as a sleepover' : 'by the hour'}`);
    } catch (e) {
      setList(l => l.map(x => x.id === st.id ? { ...x, pay_method: st.pay_method } : x));
      toast.error(e.response?.data?.message || "The pay method wasn't saved. Try again.");
    } finally { setPayBusy(b => { const n = { ...b }; delete n[st.id]; return n; }); }
  };

  const toggleActive = async (st) => {
    try { await api.put(`/service-types/${st.id}`, { isActive: !st.is_active }); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  const filtered = list.filter(s => !search.trim() ||
    s.name?.toLowerCase().includes(search.toLowerCase()) ||
    s.code?.toLowerCase().includes(search.toLowerCase()) ||
    s.category?.toLowerCase().includes(search.toLowerCase()));

  if (loading) return <div className="card overflow-hidden"><TableSkeleton rows={5} cols={6} /></div>;
  if (loadFailed && list.length === 0) {
    return <div className="card overflow-hidden"><ErrorState message="We couldn't load your service types. Check your connection, then try again." onRetry={() => { setLoading(true); load(); }} /></div>;
  }

  const showSil = form.serviceKind === 'sil' || form.serviceKind === 'group';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-slate-500">Service types are <b className="font-semibold text-slate-700">auto-created from your funding lines</b> (price guide). Rates come from the price book; here you only tweak the rules — colour, after-hours, SIL ratios, travel caps &amp; qualifications.</p>
        <button onClick={openNew} className="btn-primary flex items-center gap-1.5 shrink-0"><Plus size={14} /> New service type</button>
      </div>

      <div className="relative max-w-xs">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input className="input-field pl-8 text-sm" placeholder="Search service types…" aria-label="Search service types" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <div className="card overflow-hidden">
        {filtered.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-10">{list.length === 0 ? 'No service types yet. Add one with "New service type", or add a funding line and one is created for it.' : 'No service types match that search.'}</p>
        ) : (
          // The table scrolls sideways in its own box, so the page never has to.
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[860px]">
              <thead>
                <tr className="border-b border-slate-200 bg-white text-xs font-medium text-slate-600">
                  <th className="text-left px-4 py-3">Service type</th>
                  <th className="text-left px-3 py-3">Kind</th>
                  <th className="text-left px-3 py-3">Category</th>
                  <th className="text-left px-3 py-3">NDIS item</th>
                  <th className="text-right px-3 py-3">Staff</th>
                  <th className="text-left px-3 py-3">Pay method</th>
                  <th className="text-left px-3 py-3">Status</th>
                  <th className="px-3 py-3"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s, i) => (
                  <tr key={s.id} className={`border-b border-slate-50 last:border-0 ${i % 2 ? 'bg-slate-50/40' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full shrink-0" style={{ background: s.color || '#6366f1' }} aria-hidden="true" />
                        <span className="font-semibold text-slate-800">{s.name}</span>
                        {s.code && <span className="text-xs text-slate-500 font-mono">{s.code}</span>}
                      </div>
                    </td>
                    {/* Individual is the default kind: plain text. Anything else gets the one badge. */}
                    <td className="px-3 py-3">
                      {(s.service_kind || 'individual') === 'individual'
                        ? <span className="text-slate-500">Individual</span>
                        : <span className={QUIET_BADGE}>{KIND_LABEL[s.service_kind] || s.service_kind}</span>}
                    </td>
                    <td className="px-3 py-3 text-slate-600">{s.category || '—'}</td>
                    <td className="px-3 py-3 font-mono text-xs text-slate-500">{s.support_item_number || '—'}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-slate-700">{s.min_staff}{s.max_staff > s.min_staff ? `–${s.max_staff}` : ''}</td>
                    <td className="px-3 py-3">
                      <select aria-label={`Pay method for ${s.name}`} value={payMethodOf(s)} disabled={!!payBusy[s.id] || !canPayMethod}
                        title={canPayMethod ? undefined : needsPermission('shift_types.edit')}
                        onChange={e => changePayMethod(s, e.target.value)}
                        className={`rounded-lg border px-2 py-1 text-xs font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${payMethodOf(s) === 'sleepover' ? 'border-violet-200 bg-violet-50 text-violet-800' : 'border-blue-200 bg-blue-50 text-blue-800'}`}>
                        {PAY_METHODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </select>
                    </td>
                    {/* Active is the default: quiet text. Inactive gets a grey badge. Either one still
                        switches the service type on or off in one click, as before. */}
                    <td className="px-3 py-3">
                      <button type="button" onClick={() => toggleActive(s)} title={s.is_active ? 'Click to deactivate' : 'Click to activate'}
                        className={`rounded-full px-2 py-0.5 text-xs font-medium transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${s.is_active ? 'text-slate-500 hover:bg-slate-100 hover:text-slate-700' : `${QUIET_BADGE} hover:bg-slate-200`}`}>
                        {s.is_active ? 'Active' : 'Inactive'}
                        <span className="sr-only">{s.is_active ? ` — deactivate ${s.name}` : ` — activate ${s.name}`}</span>
                      </button>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <button type="button" onClick={() => openEdit(s)} aria-label={`Edit ${s.name}`} title="Edit"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:text-primary-600 hover:bg-primary-50 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"><Pencil size={14} /></button>
                        <button type="button" onClick={() => del(s)} aria-label={`Delete ${s.name}`} title="Delete"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-50 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <Modal wide title={modal.editing ? 'Edit service type' : 'New service type'} onClose={() => setModal(null)}>
          {/* Basics */}
          <GroupTitle>Basics</GroupTitle>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Name" required><input className="input-field" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Community Access" /></Field>
            <Field label="Code"><input className="input-field" value={form.code} onChange={e => set('code', e.target.value)} placeholder="optional short code" /></Field>
            <Field label="Colour"><input type="color" className="input-field h-10 p-1" value={form.color} onChange={e => set('color', e.target.value)} /></Field>
            <Field label="Category"><input className="input-field" value={form.categoryLabel} onChange={e => set('categoryLabel', e.target.value)} placeholder="e.g. Core Supports" /></Field>
            <Field label="Service Kind"><select className="input-field" value={form.serviceKind} onChange={e => set('serviceKind', e.target.value)}>{KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}</select></Field>
            <Field label="Claim Type"><select className="input-field" value={form.claimType} onChange={e => set('claimType', e.target.value)}>{CLAIMS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}</select></Field>
          </div>
          <div className="flex flex-wrap gap-5 pt-1">
            <Toggle checked={form.isActive} onChange={v => set('isActive', v)} label="Active" />
            <Toggle checked={form.billable} onChange={v => set('billable', v)} label="Billable" />
            <Toggle checked={form.taxable} onChange={v => set('taxable', v)} label="Taxable (GST)" />
          </div>

          {/* Pay (Pay Rates) */}
          <GroupTitle>Pay</GroupTitle>
          <Field label="Pay method" hint="Hourly is paid from the approved clock times by category. Sleepover is one fixed amount per shift; active hours before and after it go on their own Hourly shifts. Used when the company pays from Pay Rates.">
            <select className="input-field" value={form.payMethod} onChange={e => set('payMethod', e.target.value)} disabled={!canPayMethod}
              title={canPayMethod ? undefined : needsPermission('shift_types.edit')}>
              {PAY_METHODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </Field>

          {/* NDIS mapping */}
          <GroupTitle>NDIS Mapping</GroupTitle>
          <Field label="Support Item Number" hint="Pick from your loaded NDIS price guide (rate auto-flows), or type a code.">
            <NdisItemPicker value={form.supportItemNumber} onChange={v => set('supportItemNumber', v)} />
          </Field>
          <Field label="Budget Category" hint="Which client budget a shift of this type draws from. Leave blank to auto-derive from the support item number.">
            <select className="input-field" value={form.supportCategory} onChange={e => set('supportCategory', e.target.value)}>
              <option value="">Auto (from support item number)</option>
              <option value="daily_activities">Assistance With Daily Life (Personal Care / SIL)</option>
              <option value="social_community">Social &amp; Community Participation</option>
              <option value="transport">Transport</option>
              <option value="consumables">Consumables</option>
              <option value="capacity_building">Capacity Building (Therapies, Support Coord.)</option>
              <option value="capital">Capital (Assistive Tech / Home Mods)</option>
            </select>
          </Field>

          {/* Aged care mapping */}
          <GroupTitle>Aged Care Mapping (Support at Home)</GroupTitle>
          <Field label="Support at Home Item"
                 hint="Only for aged care participants. Without it a delivered shift writes no statement line, so the participant's Support at Home budget never moves. The item sets the contribution category (clinical / independence / everyday) — the price still comes from the price book on the shift.">
            <SahItemPicker value={form.agedCareItemCode} onChange={v => set('agedCareItemCode', v)} />
          </Field>

          {/* After-hours */}
          <GroupTitle>After Hours</GroupTitle>
          <div className="grid grid-cols-3 gap-3">
            <Field label="A/H Calc Method"><select className="input-field" value={form.ahCalcMethod} onChange={e => set('ahCalcMethod', e.target.value)}>{AH.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}</select></Field>
            <Field label="A/H Start"><input type="time" className="input-field" value={form.ahStart} onChange={e => set('ahStart', e.target.value)} /></Field>
            <Field label="A/H End"><input type="time" className="input-field" value={form.ahEnd} onChange={e => set('ahEnd', e.target.value)} /></Field>
          </div>

          {/* Staffing */}
          <GroupTitle>Staffing</GroupTitle>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Min Staff"><input type="number" min="1" className="input-field" value={form.minStaff} onChange={e => set('minStaff', e.target.value)} /></Field>
            <Field label="Max Staff"><input type="number" min="1" className="input-field" value={form.maxStaff} onChange={e => set('maxStaff', e.target.value)} /></Field>
          </div>

          {/* SIL */}
          {showSil && (
            <>
              <GroupTitle>SIL / Group</GroupTitle>
              <div className="grid grid-cols-2 gap-3">
                <Field label="SIL Ratio" hint="staff:participant, e.g. 1:3"><input className="input-field" value={form.silRatio} onChange={e => set('silRatio', e.target.value)} placeholder="1:3" /></Field>
                <Field label="SIL Code"><input className="input-field" value={form.silCode} onChange={e => set('silCode', e.target.value)} /></Field>
              </div>
            </>
          )}

          {/* Travel & Transport */}
          <GroupTitle>Travel &amp; Transport caps</GroupTitle>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Max Travel Km"><input type="number" step="0.1" min="0" className="input-field" value={form.maxTravelKm} onChange={e => set('maxTravelKm', e.target.value)} /></Field>
            <Field label="Max Travel Time (min)"><input type="number" min="0" className="input-field" value={form.maxTravelTimeMin} onChange={e => set('maxTravelTimeMin', e.target.value)} /></Field>
            <Field label="Max Transport Km"><input type="number" step="0.1" min="0" className="input-field" value={form.maxTransportKm} onChange={e => set('maxTransportKm', e.target.value)} /></Field>
            <Field label="Travel Km Code"><input className="input-field font-mono text-xs" value={form.travelKmCode} onChange={e => set('travelKmCode', e.target.value)} /></Field>
            <Field label="Transport Km Code"><input className="input-field font-mono text-xs" value={form.transportKmCode} onChange={e => set('transportKmCode', e.target.value)} /></Field>
            <Field label="Travel Charge %" hint="NDIS therapy 50% rule (from 1 Jul 2025)"><input type="number" min="0" max="100" className="input-field" value={form.travelChargePct} onChange={e => set('travelChargePct', e.target.value)} /></Field>
          </div>
          <Toggle checked={form.payTravel} onChange={v => set('payTravel', v)} label="Reimburse worker travel by default" />

          {/* Qualifications */}
          <GroupTitle>Qualification Restriction</GroupTitle>
          <Toggle checked={form.restrictByQual} onChange={v => set('restrictByQual', v)} label="Require a qualification to be rostered on this service" />
          {form.restrictByQual && (
            <div>
              <div className="flex gap-2">
                <input className="input-field flex-1" placeholder="e.g. First Aid" value={qualInput}
                  onChange={e => setQualInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addQual(); } }} />
                <button type="button" onClick={addQual} className="btn-secondary btn-sm">Add</button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {form.quals.map(q => (
                  <span key={q} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-700 border border-primary-200">
                    {q}<button type="button" onClick={() => set('quals', form.quals.filter(x => x !== q))} aria-label={`Remove ${q}`} className="hover:text-red-500"><X size={11} /></button>
                  </span>
                ))}
                {form.quals.length === 0 && <span className="text-xs text-slate-400">No qualifications added.</span>}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2 border-t border-slate-100 mt-2">
            <button onClick={() => setModal(null)} className="btn-secondary btn-sm">Cancel</button>
            <button onClick={save} disabled={saving} className="btn-primary btn-sm">{saving ? 'Saving…' : modal.editing ? 'Save changes' : 'Create service type'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// Searchable NDIS-item picker — type a code directly, or search the loaded price guide and
// pick (rate shown). Picking sets the support item number; the budget category auto-derives.
function NdisItemPicker({ value, onChange }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    const id = setTimeout(() => {
      api.get(`/price-books/items?search=${encodeURIComponent(q)}`).then(r => setResults(r.data || [])).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(id);
  }, [q]);
  return (
    <div className="relative">
      <input className="input-field font-mono text-sm" value={value || ''} onChange={e => onChange(e.target.value)} placeholder="01_011_0107_1_1" />
      <div className="relative mt-1.5">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input className="input-field text-sm pl-8" placeholder="Search the price guide (name or number)…" value={q}
          onChange={e => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} />
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-56 overflow-auto">
          {results.map(it => (
            <button key={it.supportItemNumber} type="button"
              onClick={() => { onChange(it.supportItemNumber); setQ(''); setResults([]); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-50 last:border-0">
              <div className="text-sm text-slate-700 truncate">{it.supportItemName}</div>
              <div className="text-[11px] text-slate-400 flex justify-between"><span className="font-mono">{it.supportItemNumber}</span>{it.weekdayRate != null && <span className="text-emerald-600 font-semibold">${it.weekdayRate.toFixed(2)}/h</span>}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Pick a Support at Home item from the org's loaded SAH service list. A free-text box stays
// available so a code can be set before the list is imported. Empty = not an aged care service.
function SahItemPicker({ value, onChange }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get('/aged-care/service-items').then(r => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
  }, []);
  const known = items.some(i => i.code === value);
  return (
    <div className="space-y-1.5">
      {items.length > 0 && (
        <select className="input-field" value={known ? value : ''} onChange={e => onChange(e.target.value)}>
          <option value="">Not an aged care service</option>
          {items.map(i => <option key={i.id || i.code} value={i.code}>{i.name} — {i.category}</option>)}
        </select>
      )}
      <input className="input-field font-mono text-sm" value={value || ''} onChange={e => onChange(e.target.value)}
        placeholder={items.length ? 'or type a code' : 'e.g. SAH-IN-PERSONAL (load the service list in Aged Care → Services)'} />
    </div>
  );
}
